import { Box, Card, CardContent, Skeleton } from '@mui/material';
import { useMemo } from 'react';

interface LoaderProps {
  variant?: 'list' | 'table' | 'chart' | 'card' | 'text' | 'dashboard';
  count?: number;
  lines?: number;
  height?: string | number;
  width?: string | number;
  animation?: 'pulse' | 'wave' | false;
}

export const Loader = ({
  variant = 'text',
  count = 1,
  height = 20,
  width = '100%',
  lines = 3,
  animation = 'wave',
}: LoaderProps) => {
  void count;

  // Generate stable keys for arrays to avoid linting issues
  const listItems = useMemo(() => Array.from({ length: lines }, (_, i) => i), [lines]);
  const headerItems = useMemo(() => Array.from({ length: 4 }, (_, i) => i), []);
  const cardItems = useMemo(() => Array.from({ length: 3 }, (_, i) => i), []);

  switch (variant) {
    case 'chart':
      return (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="text" width="40%" height={32} animation={animation} sx={{ mb: 2 }} />
          <Box sx={{ height, backgroundColor: '#f5f5f5', borderRadius: 1, p: 2 }}>
            <Skeleton variant="rectangular" width="100%" height="80%" animation={animation} />
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Skeleton variant="text" width="15%" height={20} animation={animation} />
              <Skeleton variant="text" width="15%" height={20} animation={animation} />
              <Skeleton variant="text" width="15%" height={20} animation={animation} />
            </Box>
          </Box>
        </Box>
      );

    case 'list':
      return (
        <Box>
          {listItems.map(index => (
            <Box key={`list-item-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                animation={animation}
                sx={{ mr: 2 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton
                  variant="text"
                  width="60%"
                  height={16}
                  animation={animation}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton variant="text" width="40%" height={14} animation={animation} />
              </Box>
            </Box>
          ))}
        </Box>
      );

    case 'table':
      return (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            {headerItems.map(index => (
              <Skeleton
                key={`table-header-${index}`}
                variant="text"
                width="25%"
                height={24}
                animation={animation}
                sx={{ fontWeight: 'bold' }}
              />
            ))}
          </Box>
          {listItems.map(rowIndex => (
            <Box key={`table-row-${rowIndex}`} sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
              {headerItems.map(cellIndex => (
                <Skeleton
                  key={`table-cell-${rowIndex}-${cellIndex}`}
                  variant="text"
                  width="25%"
                  height={20}
                  animation={animation}
                />
              ))}
            </Box>
          ))}
        </Box>
      );

    case 'card':
      return (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {cardItems.map(index => (
              <Card key={`card-${index}`} sx={{ flex: 1 }}>
                <CardContent>
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={24}
                    animation={animation}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    animation={animation}
                    sx={{ mb: 2 }}
                  />
                  <Skeleton variant="rectangular" width="100%" height={60} animation={animation} />
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      );

    case 'dashboard':
      return (
        <Box sx={{ p: 3 }}>
          <Skeleton variant="text" width="30%" height={40} animation={animation} sx={{ mb: 3 }} />
          <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
            {cardItems.map(index => (
              <Box key={`dashboard-card-${index}`} sx={{ flex: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={120}
                  animation={animation}
                  sx={{ mb: 1, borderRadius: 1 }}
                />
                <Skeleton variant="text" width="70%" height={20} animation={animation} />
              </Box>
            ))}
          </Box>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={300}
            animation={animation}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      );

    default:
      return (
        <Box>
          {listItems.map(index => (
            <Skeleton
              key={`text-line-${index}`}
              variant="text"
              width={index === lines - 1 ? '70%' : width}
              height={height}
              animation={animation}
              sx={{ mb: 0.5 }}
            />
          ))}
        </Box>
      );
  }
};

export const ChartSkeleton = () => <Loader variant="chart" height={400} />;
